
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import {
  FridgeDetailsDoorOpenInfo,
  FridgeDetailsPushSettings
} from "@/types/fridgeDetails";

import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil from "@/utils/common-utils";
import FridgeDetailsSelectButtons from "@/components/fridgeDetails/FridgeDetailsSelectButtons.vue"
import FridgeDoorMonitorDaily from "@/components/fridgeDetails/FridgeDoorMonitorDaily.vue";
import FridgeDoorMonitorWeekly from "@/components/fridgeDetails/FridgeDoorMonitorWeekly.vue";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import router from "@/router";

/** スクロールイベントのIF */
interface HTMLElementEvent<T extends HTMLElement> extends Event {
  target: T;
}

export default defineComponent({
  name: "FridgeDoorMonitorPage",
  components: { 
    FridgeDetailsSelectButtons, 
    FridgeDoorMonitorDaily, 
    FridgeDoorMonitorWeekly, 
    CommonDesignDialog },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "ドアモニター"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const setScrollTo = (position: number) => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage setScrollTo scrollWidth = " + scroll.value?.scrollWidth);
      if (scroll.value != undefined) {
        NativeClient.sendDebugLog("FridgeDoorMonitorPage setScrollTo left = " + (scroll.value?.scrollWidth / 2) * position);
        scroll.value.scrollTo({ top: 0, left: (scroll.value?.scrollWidth / 2) * position });
      }
    };

    // 日（=0）／週（=1）
    const selectDailyWeekly = ref(0);
    const selectDaily = () => {
      if (selectDailyWeekly.value != 0) {
        selectDailyWeekly.value = 0;
        NativeClient.sendDebugLog("FridgeDoorMonitorPage selectDaily start");
        setScrollTo(0);
        store.dispatch("fridgeDetails/updateDoorMonitorTabIndex", 0);
      }
    };
    const selectWeekly =() => {
      if (selectDailyWeekly.value != 1) {
        selectDailyWeekly.value = 1;
        NativeClient.sendDebugLog("FridgeDoorMonitorPage selectWeekly start");
        setScrollTo(1);
        store.dispatch("fridgeDetails/updateDoorMonitorTabIndex", 1);
      }
    };

    // PUSH設定のタイトル
    const pushSettingsTitle = "通知設定";
    const pushSettingsValue = computed(() => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage pushSettingsValue start");
      const settings: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettings"];
      if (settings != null) {
        const doorOpenSettings = settings.param_list.find(element => element.param_name == 'doorOpenInfo');
        if (doorOpenSettings != undefined) {
          if (!doorOpenSettings.param_value) {
            return "通知なし";
          } else {
            return `通知:${doorOpenSettings.param_time}時間ごと`
          }
        }
        return "通知なし";
      }
      NativeClient.sendDebugLog("FridgeDoorMonitorPage pushSettingsValue return null");
      return null;
    });

    // 一覧更新
    const getDoorMonitorInfo = async () => {
      NativeClient.sendDebugLog("getDoorMonitorInfo getDoorOpenInfo()");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await Promise.all([
        store.dispatch("fridgeDetails/getDoorOpenInfo"),
        store.dispatch("fridgeDetails/getPushSettings"),
      ]).then(result => {
        store.dispatch("common/stopLoading");
        updateDisplay();
      });
    };

    // グラフの情報
    const initialDailyTabIndex = ref(0);
    const dailyList = computed(() => {
      const result: FridgeDetailsDoorOpenInfo | null = store.getters["fridgeDetails/getDoorOpenInfo"];
      NativeClient.sendDebugLog("FridgeDoorMonitorPage dailyList result = " + JSON.stringify(result));
      if (result != null) {
        if (result.daily.door_open_list.length > 0) {
          const dailyTabIndex = store.getters["fridgeDetails/getDoorMonitorDailyTabIndex"];
          NativeClient.sendDebugLog("FridgeDoorMonitorPage dailyList() dailyTabIndex = " + dailyTabIndex);
          if (dailyTabIndex == null) {
            // 初期状態なのでラストを表示
            initialDailyTabIndex.value = result.daily.door_open_list.length - 1;
          }
        }
        return result.daily.door_open_list;
      }
      NativeClient.sendDebugLog("FridgeDoorMonitorPage dailyList no element");
      return [];
    });
    const changeDailyIndex = (index: number) => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage changeDailyIndex index = " + index);
      store.dispatch("fridgeDetails/updateDoorMonitorDailyTabIndex", index);
    };
    const weeklyList = computed(() => {
      const result: FridgeDetailsDoorOpenInfo | null = store.getters["fridgeDetails/getDoorOpenInfo"];
      NativeClient.sendDebugLog("FridgeDoorMonitorPage weeklyList result = " + JSON.stringify(result));
      if (result != null) {
        return result.weekly.door_open_list;
      }
      NativeClient.sendDebugLog("FridgeDoorMonitorPage weeklyList no element");
      return [];
    });

    // const tabIndex = store.getters["fridgeDetails/getDoorMonitorTabIndex"];
    // NativeClient.sendDebugLog("FridgeDoorMonitorPage setup() tabIndex = " + tabIndex);
    // if (tabIndex != null && tabIndex != 0) {
    //   NativeClient.sendDebugLog("FridgeDoorMonitorPage onMounted() tabIndex not 0");
    //   setScrollTo(tabIndex);
    // }

    // PUSH設定へ
    const isShowNotificationAlert = ref(false);
    const notificationAlertType = ref(2);
    const notificationAlertTitle = "";
    const notificationAlertMessage = "ご利用のスマートフォンの「設定」＞「通知」から「Live Pantry」の通知を許可してください。";
    const notificationAlertNegativeButtonTitle = "閉じる";
    const notificationAlertNegativeButtonHandler = () => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage notificationAlertNegativeButtonHandler()");
      isShowNotificationAlert.value = false;
    };
    const notificationSettingsCallback = (value: boolean) => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage notificationSettingsCallback value = " + value);
      if (value) {
        router.push('/fridgePushSettings');
      } else {
        isShowNotificationAlert.value = true;
      }
    };
    const clickPushSetting = () => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage clickPushSetting()");
      NativeClient.requestGetNotificationSetting();
    };

    // 表示の制御
    //let isDisplayed = false;
    onMounted(() => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);

      const tabIndex = store.getters["fridgeDetails/getDoorMonitorTabIndex"];
      NativeClient.sendDebugLog("FridgeDoorMonitorPage onMounted() tabIndex = " + tabIndex);
      if (tabIndex != null && tabIndex != 0) {
        NativeClient.sendDebugLog("FridgeDoorMonitorPage onMounted() tabIndex not 0");
        selectDailyWeekly.value = tabIndex;
        setScrollTo(tabIndex);
        updateDisplay();
      }

      const dailyTabIndex = store.getters["fridgeDetails/getDoorMonitorDailyTabIndex"];
      NativeClient.sendDebugLog("FridgeDoorMonitorPage onMounted() dailyTabIndex = " + dailyTabIndex);
      if (dailyTabIndex != null && dailyTabIndex != 0) {
        NativeClient.sendDebugLog("FridgeDoorMonitorPage onMounted() dailyTabIndex not 0");
        initialDailyTabIndex.value = dailyTabIndex;
        updateDisplay();
      }

      getDoorMonitorInfo();

      // 通知設定取得コールバック登録
      NativeClient.registGetNotificationSettingListener(notificationSettingsCallback);
    });
    onUnmounted(() => {
      NativeClient.sendDebugLog("FridgeDoorMonitorPage onUnmounted()");
    });

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // getDoorMonitorInfo();
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    // ドアアラーム
    const isShowDoorAlarm = ref(false);
    const doorAlarmType = 9;
    const doorAlarmTitle = "ドアアラームが鳴りました";
    const doorAlarmMessage = "5分以上ドアを開けっ放しの時に\nアラームでお知らせします。";
    const doorAlarmOKButtonTitle = "OK";
    const doorAlarmOKHandler = () => {
      isShowDoorAlarm.value = false;
    };

    // ドアアラーム表示
    const displayDoorAlarm = () => {
      isShowDoorAlarm.value = true;
    };
    
    return {
      pushSettingsTitle,
      pushSettingsValue,
      selectDailyWeekly,
      selectDaily,
      selectWeekly,
      scroll,
      initialDailyTabIndex,
      changeDailyIndex,
      dailyList,
      weeklyList,
      updateDisplayKey,
      isShowNotificationAlert,
      notificationAlertType,
      notificationAlertTitle,
      notificationAlertMessage,
      notificationAlertNegativeButtonTitle,
      notificationAlertNegativeButtonHandler,
      clickPushSetting,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler,
      isShowDoorAlarm,
      doorAlarmType,
      doorAlarmTitle,
      doorAlarmMessage,
      doorAlarmOKButtonTitle,
      doorAlarmOKHandler,
      displayDoorAlarm
    };
  }
});
